/**
 * ตัวเลือก "ลูกค้า" ของสินค้า (ระดับสินค้า) — ใช้ร่วมกันในฟอร์มสร้าง แก้ไข และขอเปลี่ยนแปลง
 *
 * โหลดเฉพาะลูกค้า `ACTIVE` จาก `GET /v1/customers?status=ACTIVE` ที่หน้า Customer Master
 * ใช้อยู่แล้ว (ไม่เพิ่ม endpoint) ค่าว่าง = ไม่ผูกลูกค้า; ล้างค่าได้ด้วยปุ่ม ✕
 * ถ้าโหลดรายการไม่สำเร็จ กล่องยังแสดงรหัสที่ถืออยู่และล้างค่าได้ — การตัดสินว่ารหัสใช้ได้หรือไม่
 * เป็นของเซิร์ฟเวอร์ (422 `CUSTOMER_NOT_FOUND` / `CUSTOMER_NOT_ACTIVE`)
 */

import { useEffect, useState } from 'react';

import { SearchableSelect, type SearchableSelectOption } from '../components/SearchableSelect';
import { fetchCustomers } from '../../lib/customers/client';

export interface CustomerSelectProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly ariaLabel: string;
  readonly testId: string;
}

const MAX_PAGES = 10;

export function CustomerSelect({
  apiBaseUrl,
  fetchImpl,
  value,
  onChange,
  ariaLabel,
  testId,
}: CustomerSelectProps) {
  const [options, setOptions] = useState<readonly SearchableSelectOption[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    void (async () => {
      const collected: SearchableSelectOption[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const result = await fetchCustomers(
          { status: 'ACTIVE', limit: 200, cursor },
          { apiBaseUrl, fetchImpl, signal: controller.signal },
        );
        if (result.status !== 'ready') {
          break;
        }
        for (const customer of result.page.items) {
          if (customer.status === 'ACTIVE') {
            collected.push({
              value: customer.customer_code,
              label: `${customer.customer_code} — ${customer.customer_name}`,
            });
          }
        }
        cursor = result.page.next_cursor ?? undefined;
        if (cursor === undefined) {
          break;
        }
      }
      if (active) {
        setOptions(collected);
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl]);

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder="ยังไม่ระบุลูกค้า"
      emptyText="ไม่พบลูกค้าที่ใช้งานอยู่"
      clearable
      ariaLabel={ariaLabel}
      testId={testId}
    />
  );
}
